import { useState, useRef, useCallback, type DragEvent } from "react";

const ACCEPTED_TYPES = [
  "application/pdf",
  "text/csv",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
];

const ACCEPTED_EXTENSIONS = [".pdf", ".csv", ".xlsx", ".xls"];

interface DropZoneProps {
  /** Called when valid file(s) are dropped or selected. */
  onFiles: (files: File[]) => void;
  /** Whether an upload is currently in progress — disables interaction. */
  disabled?: boolean;
}

/**
 * Reusable drag-and-drop file zone that accepts PDF, CSV, and XLSX files.
 * Also provides a "Browse files" button as fallback.
 * Supports single and multi-file selection.
 */
export default function DropZone({ onFiles, disabled = false }: DropZoneProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDragEnter = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) setIsDragOver(true);
    },
    [disabled],
  );

  const handleDragLeave = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      // Only set false when leaving the drop zone itself (not a child)
      if (e.currentTarget === e.target) {
        setIsDragOver(false);
      }
    },
    [],
  );

  const handleDragOver = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
    },
    [],
  );

  const filterValidFiles = useCallback((fileList: FileList): File[] => {
    return Array.from(fileList).filter((file) => {
      const ext = "." + file.name.split(".").pop()?.toLowerCase();
      return (
        ACCEPTED_TYPES.includes(file.type) ||
        ACCEPTED_EXTENSIONS.includes(ext)
      );
    });
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      if (disabled) return;

      const valid = filterValidFiles(e.dataTransfer.files);
      if (valid.length > 0) {
        onFiles(valid);
      }
    },
    [disabled, filterValidFiles, onFiles],
  );

  const handleBrowse = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!e.target.files) return;
      const valid = filterValidFiles(e.target.files);
      if (valid.length > 0) {
        onFiles(valid);
      }
      // Reset so the same file can be re-selected
      e.target.value = "";
    },
    [filterValidFiles, onFiles],
  );

  return (
    <div
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      className={`relative flex flex-col items-center justify-center rounded-xl border-2 border-dashed p-12 transition-colors ${
        disabled
          ? "cursor-not-allowed border-gray-200 bg-gray-50 opacity-60"
          : isDragOver
            ? "border-blue-500 bg-blue-50"
            : "border-gray-300 bg-white hover:border-gray-400"
      }`}
    >
      {/* Upload icon */}
      <svg
        className={`mb-4 h-12 w-12 ${isDragOver ? "text-blue-500" : "text-gray-400"}`}
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.338-2.32 3 3 0 013.4 4.598A4.49 4.49 0 0118 19.5H6.75z"
        />
      </svg>

      <p className="mb-1 text-sm font-medium text-gray-700">
        {isDragOver ? "Drop files here" : "Drag and drop invoice files here"}
      </p>
      <p className="mb-4 text-xs text-gray-500">PDF, CSV, or XLSX</p>

      <button
        type="button"
        onClick={handleBrowse}
        disabled={disabled}
        className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
      >
        Browse files
      </button>

      <input
        ref={inputRef}
        type="file"
        multiple
        accept=".pdf,.csv,.xlsx,.xls"
        onChange={handleInputChange}
        className="hidden"
      />
    </div>
  );
}
